// #258 R5.3 — inline glyphs for the left-rail canvas-annotation groups: the decorative shapes
// (ellipse / rect / triangle / star) and the mechanism curved arrows (full head = electron
// pair, half head = single electron / fish-hook, multi = multi-headed). Drawn here rather than
// vendored as Material Symbols so the arc + head geometry matches what the canvas renders.
// Colour follows `currentColor`; the host control carries the accessible name.

import { useId } from 'react';

export type ShapeGlyphVariant = 'ellipse' | 'rect' | 'triangle' | 'star' | 'path';
export type ArrowGlyphVariant = 'full' | 'half' | 'multi';

function starPoints(): string {
  const cx = 16;
  const cy = 16;
  return Array.from({ length: 10 }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    const radius = index % 2 === 0 ? 12 : 5;
    return `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`;
  }).join(' ');
}

export function ShapeGlyph({ variant }: { readonly variant: ShapeGlyphVariant }) {
  return (
    <svg className="annotation-glyph" viewBox="0 0 32 32" aria-hidden="true" focusable="false" data-shape={variant}>
      {variant === 'ellipse' ? <ellipse cx="16" cy="16" rx="13" ry="9" /> : null}
      {variant === 'rect' ? <rect x="4" y="7" width="24" height="18" rx="2" /> : null}
      {variant === 'triangle' ? <polygon points="16,4 28,27 4,27" /> : null}
      {variant === 'star' ? <polygon points={starPoints()} /> : null}
      {variant === 'path' ? <path d="M 4 25 C 7 3 24 30 28 7" fill="none" /> : null}
    </svg>
  );
}

export function ArrowGlyph({ variant }: { readonly variant: ArrowGlyphVariant }) {
  const markerId = useId();

  if (variant === 'full') {
    return (
      <svg className="annotation-glyph" viewBox="0 0 64 63" aria-hidden="true" focusable="false" data-arrow={variant}>
        <defs>
          <marker id={markerId} markerWidth="10" markerHeight="10" refX="5" refY="5" orient="auto">
            <path d="M0 2 L9 5 L0 8 L3 5 Z" fill="currentColor" stroke="none" />
          </marker>
        </defs>
        {/* Reference SVG cubic; auto orientation aligns the full head with the end tangent. */}
        <path d="M5 47 C1 5 45 5 55 39" fill="none" strokeWidth="3" markerEnd={`url(#${markerId})`} />
      </svg>
    );
  }

  if (variant === 'half') {
    return (
      <svg className="annotation-glyph" viewBox="0 0 64 63" aria-hidden="true" focusable="false" data-arrow={variant}>
        <defs>
          <marker id={markerId} markerWidth="13" markerHeight="13" refX="3" refY="5" orient="auto">
            <path d="M0 0 L10 5 L2 5.53 L3 5 Z" fill="currentColor" stroke="none" />
          </marker>
        </defs>
        {/* Reference SVG cubic and single barb, aligned with the end tangent. */}
        <path d="M5 47 C1 5 41 5 52 36" fill="none" strokeWidth="2.5" markerEnd={`url(#${markerId})`} />
      </svg>
    );
  }

  return (
    <svg className="annotation-glyph" viewBox="0 0 250 150" aria-hidden="true" focusable="false" data-arrow={variant}>
      <defs>
        <marker id={markerId} viewBox="0 0 5 5" markerWidth="5" markerHeight="5" refX="3.87" refY="2.5" orient="auto">
          <path d="M0 0 L5 2.5 L0 5 Z" fill="currentColor" stroke="none" />
        </marker>
      </defs>
      {/* Cubic approximations of the reference's radius-183.03 arcs retain their
          end tangents and meet the straight shaft at the shared midpoint. */}
      <g fill="none" strokeWidth="2.5" markerEnd={`url(#${markerId})`}>
        <path d="M14 75 L236 75" />
        <path d="M14 37.5 C79.554 87.5 170.446 87.5 236 37.5" />
        <path d="M14 112.5 C79.554 62.5 170.446 62.5 236 112.5" />
      </g>
    </svg>
  );
}
